import type {
	AvailabilityChange,
	AvailabilityPart,
	AvailabilityStatus,
	MyAvailability,
	NewShift,
	ScheduleAttention,
	ScheduleWeek,
	ShiftFillSource,
	ShiftPatch,
	ShiftPosition,
	StaffingNeed,
	TemplateInput,
} from '../../types/schedule'
import { request } from './http'

const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) })

export const getScheduleWeek = (weekStart: string | null) => request<ScheduleWeek>(weekStart ? `/schedule?week=${weekStart}` : '/schedule')

export const getScheduleAttention = () => request<ScheduleAttention>('/schedule/attention')

export const createShift = (body: NewShift) => request<{ id: number }>('/schedule/shifts', json('POST', body))

export const createShifts = (shifts: NewShift[]) => request<{ created: number }>('/schedule/shifts/batch', json('POST', { shifts }))

export const putStaffingNeeds = (needs: StaffingNeed[]) => request<{ updated: number }>('/schedule/needs', json('PUT', { needs }))

export const putMemberProfile = (userId: string, body: { is_scheduled: boolean; positions: ShiftPosition[]; can_close: boolean }) =>
	request<{ user_id: string }>(`/schedule/members/${userId}`, json('PUT', body))

export const patchShift = (id: number, body: ShiftPatch) => request<{ id: number }>(`/schedule/shifts/${id}`, json('PATCH', body))

export const deleteShift = (id: number) => request<{ id: number }>(`/schedule/shifts/${id}`, json('DELETE'))

export const fillWeek = (weekStart: string, source: ShiftFillSource) =>
	request<{ created: number }>(`/schedule/weeks/${weekStart}/fill`, json('POST', { source }))

export const publishWeek = (weekStart: string) => request<{ published: number }>(`/schedule/weeks/${weekStart}/publish`, json('POST'))

export const assignDutiesByRule = (date: string) => request<{ date: string }>(`/schedule/days/${date}/duties`, json('POST'))

export const chooseShiftStart = ({ id, startTime }: { id: number; startTime: string }) =>
	request<{ id: number }>(`/schedule/shifts/${id}/start`, json('POST', { start_time: startTime }))

export const offerShift = (id: number) => request<{ id: number }>(`/schedule/shifts/${id}/offer`, json('POST'))

export const withdrawOffer = (id: number) => request<{ id: number }>(`/schedule/shifts/${id}/offer`, json('DELETE'))

export const requestShift = (id: number) => request<{ id: number }>(`/schedule/shifts/${id}/requests`, json('POST'))

export const approveShiftRequest = (id: number) => request<{ id: number }>(`/schedule/requests/${id}/approve`, json('POST'))

export const declineShiftRequest = (id: number) => request<{ id: number }>(`/schedule/requests/${id}/decline`, json('POST'))

export const withdrawShiftRequest = (id: number) => request<{ id: number }>(`/schedule/requests/${id}`, json('DELETE'))

export const createShiftTemplate = (body: TemplateInput) => request<{ id: number }>('/schedule/templates', json('POST', body))

export const updateShiftTemplate = (id: number, body: TemplateInput) =>
	request<{ id: number }>(`/schedule/templates/${id}`, json('PUT', body))

export const archiveShiftTemplate = (id: number) => request<{ id: number }>(`/schedule/templates/${id}`, json('DELETE'))

export const getMyAvailability = (from: string | null) =>
	request<MyAvailability>(from ? `/schedule/availability?from=${from}` : '/schedule/availability')

export const putAvailabilityPattern = (days: { weekday: number; status: AvailabilityStatus; part: AvailabilityPart }[]) =>
	request<{ updated: number }>('/schedule/availability/pattern', json('PUT', { days }))

export const putAvailabilityDates = (change: AvailabilityChange) =>
	request<{ updated: number }>('/schedule/availability/dates', json('PUT', change))

export const putWishHours = (weeklyHours: number | null) =>
	request<{ weekly_hours: number | null }>('/schedule/availability/hours', json('PUT', { weekly_hours: weeklyHours }))
